import { useState, useEffect, useRef, useCallback } from 'react';

// Engineering-style callouts — per view key, anchored precisely at the dot position
export const CALLOUTS = {
  full: [
    { top: '26%', dot: '41%', dir: 'left', label: 'Toray T700 Carbon Face' },
    { top: '47%', dot: '58%', dir: 'right', label: '16mm Honeycomb Core' },
    { top: '64%', dot: '60%', dir: 'right', label: 'Aero Edge Guard' },
  ],
  face: [
    { top: '28%', dot: '56%', dir: 'right', label: 'Raw Carbon Weave' },
    { top: '54%', dot: '45%', dir: 'left', label: 'Embossed DYSSC Graphic' },
    { top: '72%', dot: '58%', dir: 'right', label: 'Max-Spin Texture' },
  ],
  side: [
    { top: '28%', dot: '52%', dir: 'right', label: '16mm Aero Profile' },
    { top: '54%', dot: '48%', dir: 'left', label: 'Gradient Edge Guard' },
  ],
  grip: [
    { top: '26%', dot: '57%', dir: 'right', label: 'DYSSC Lock Ring' },
    { top: '60%', dot: '46%', dir: 'left', label: 'Perforated Comfort Grip' },
  ],
  handle: [
    { top: '38%', dot: '50%', dir: 'left', label: 'Ergonomic Butt Cap' },
    { top: '62%', dot: '55%', dir: 'right', label: 'Extended Handle' },
  ],
  detail: [
    { top: '32%', dot: '52%', dir: 'right', label: 'Premium Finish' },
    { top: '62%', dot: '46%', dir: 'left', label: 'USAP Approved' },
  ],
  sleeve: [
    { top: '14%', dot: '56%', dir: 'right', label: '16mm Optimal Thickness' },
    { top: '34%', dot: '44%', dir: 'left', label: 'T700 Carbon Fibre Surface' },
    { top: '56%', dot: '53%', dir: 'right', label: 'PBCoR .43 Approved' },
    { top: '78%', dot: '48%', dir: 'left', label: 'Perforated Comfort Grip' },
  ],
  pf_full: [
    { top: '24%', dot: '47%', dir: 'left', label: 'Toray T700 Carbon Face' },
    { top: '44%', dot: '55%', dir: 'right', label: '16mm ProFoam Core' },
    { top: '62%', dot: '56%', dir: 'right', label: 'Blue Gradient Edge Guard' },
  ],
  pf_face: [
    { top: '30%', dot: '55%', dir: 'right', label: 'Embossed ProFoam Graphic' },
    { top: '58%', dot: '46%', dir: 'left', label: 'Raw Carbon Weave' },
  ],
  pf_angle: [
    { top: '30%', dot: '54%', dir: 'right', label: 'Shock-dispersing Foam' },
    { top: '62%', dot: '46%', dir: 'left', label: 'Max-Spin Texture' },
  ],
  pf_grip: [
    { top: '32%', dot: '52%', dir: 'right', label: 'DYSSC Lock Ring' },
    { top: '64%', dot: '46%', dir: 'left', label: 'Perforated Comfort Grip' },
  ],
  pf_side: [
    { top: '28%', dot: '52%', dir: 'right', label: '16mm Foam Profile' },
    { top: '56%', dot: '48%', dir: 'left', label: 'Gradient Edge Guard' },
  ],
};

const HEX_VIEW_META = {
  hexcore_n1: { key: 'full', label: 'Full view' },
  hexcore_n2: { key: 'face', label: 'Face' },
  hexcore_n3b: { key: 'grip', label: 'Grip' },
  hexcore_n4: { key: 'handle', label: 'Handle' },
  hexcore_n5: { key: 'side', label: 'Side profile' },
  hexcore_n6: { key: 'detail', label: 'Detail', fade: true },
  hexcore_sleeve: { key: 'sleeve', label: 'Pro Series', fade: true },
};

const PF_VIEW_META = {
  profoam_n1: { key: 'pf_full', label: 'Full view' },
  profoam_n2: { key: 'pf_face', label: 'Face' },
  profoam_n3: { key: 'pf_angle', label: 'Angle' },
  profoam_n4: { key: 'pf_grip', label: 'Grip' },
  profoam_n5: { key: 'pf_side', label: 'Side profile' },
};
const VIEW_META = { ...HEX_VIEW_META, ...PF_VIEW_META };

export const viewsFromImages = (images = []) =>
  images
    .filter((src) => !src.endsWith('.mp4'))
    .map((src, i) => {
      const match = Object.keys(VIEW_META).find((k) => src.includes(k));
      const meta = match ? VIEW_META[match] : { key: `view-${i}`, label: `View ${i + 1}` };
      return { ...meta, src };
    });

// Interactive white-studio product stage: 3D mouse tilt, tracking spotlight,
// hex blueprint rings, zoom-crossfade gallery, per-view callouts, filmstrip dock.
// Product photos are rendered UNTOUCHED — presentation effects only.
export const PaddleStage = ({
  views,
  alt = 'DYSSC pickleball paddle',
  autoCycle = false,
  showHint = false,
  onStageClick,
  heightClass = 'h-[480px] md:h-[620px]',
  accent = '#E02128',
}) => {
  const [view, setView] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hinted, setHinted] = useState(false);
  const stageRef = useRef(null);

  useEffect(() => {
    if (!autoCycle || paused) return;
    const t = setInterval(() => setView((v) => (v + 1) % views.length), 4500);
    return () => clearInterval(t);
  }, [autoCycle, paused, views.length]);

  const onMove = useCallback((e) => {
    const el = stageRef.current;
    if (!el) return;
    setHinted(true);
    setPaused(true);
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--ry', `${(x * 18).toFixed(2)}deg`);
    el.style.setProperty('--rx', `${(-y * 14).toFixed(2)}deg`);
    el.style.setProperty('--sx', `${50 + x * 26}%`);
    el.style.setProperty('--sy', `${52 + y * 20}%`);
  }, []);
  const onLeave = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--sx', '50%');
    el.style.setProperty('--sy', '52%');
  }, []);

  return (
    <div
      ref={stageRef}
      className={`relative ${heightClass} ${onStageClick ? 'cursor-pointer' : ''}`}
      style={{ perspective: '1400px' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onStageClick}
      data-testid="showcase-stage"
    >
      {/* mouse-tracking studio spotlight */}
      <div
        className="absolute inset-x-4 top-2 bottom-16 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 55% 45% at var(--sx,50%) var(--sy,52%), #ECECEF 0%, rgba(236,236,239,0) 70%)',
          transition: 'background 0.3s ease-out',
        }}
      />

      {/* slow-rotating hex blueprint rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 200 200" className="w-[68%] max-w-[430px] opacity-70 animate-[spin_50s_linear_infinite]">
          <polygon points="100,8 180,54 180,146 100,192 20,146 20,54" fill="none" stroke="#E6E6E9" strokeWidth="0.8" strokeDasharray="5 7" />
        </svg>
        <svg viewBox="0 0 200 200" className="absolute w-[52%] max-w-[330px] opacity-60 animate-[spin_70s_linear_infinite_reverse]">
          <polygon points="100,8 180,54 180,146 100,192 20,146 20,54" fill="none" stroke="#ECECEF" strokeWidth="0.8" strokeDasharray="2 6" />
        </svg>
      </div>

      {/* tilt wrapper — zoom-crossfading original photos */}
      <div
        className="absolute inset-x-0 top-0 bottom-16 will-change-transform"
        style={{
          transform: 'rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.25s ease-out',
        }}
      >
        {views.map((v, i) => (
          <div
            key={v.key}
            className="absolute inset-0"
            style={{
              opacity: i === view ? 1 : 0,
              transform: i === view ? 'scale(1)' : 'scale(0.94)',
              transition: 'opacity 0.55s cubic-bezier(0.4,0,0.2,1), transform 0.55s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            <img
              src={v.src}
              alt={`${alt} — ${v.label}`}
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'auto'}
              decoding="async"
              className="absolute inset-0 m-auto max-h-full max-w-[92%] md:max-w-[78%] object-contain animate-[hex-float_7s_ease-in-out_infinite]"
              style={
                v.fade
                  ? {}
                  : {
                      filter: 'drop-shadow(0 30px 34px rgba(10,10,10,0.20)) drop-shadow(0 6px 10px rgba(10,10,10,0.22))',
                      WebkitBoxReflect: 'below -22px linear-gradient(transparent 70%, rgba(0,0,0,0.12))',
                    }
              }
            />
          </div>
        ))}
      </div>

      {/* engineering callouts — per view, staggered line-draw entrance */}
      <div key={view} className="absolute inset-x-0 top-0 bottom-16 pointer-events-none z-20" data-testid="showcase-callouts">
        {(CALLOUTS[views[view]?.key] || []).map((c, i) => (
          <div
            key={c.label}
            className="absolute flex items-center opacity-0 animate-[callout-in_0.6s_cubic-bezier(0.22,1,0.36,1)_forwards]"
            style={{
              top: c.top,
              left: c.dot,
              transform: c.dir === 'left' ? 'translateX(-100%)' : 'none',
              '--co-shift': c.dir === 'left' ? '-100%' : '0%',
              animationDelay: `${600 + i * 220}ms`,
            }}
          >
            {c.dir === 'left' && (
              <>
                <span className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.14em] sm:tracking-[0.18em] max-w-[34vw] sm:max-w-none whitespace-normal sm:whitespace-nowrap text-right leading-tight text-neutral-600 bg-white/90 backdrop-blur-sm px-1.5 sm:px-2 py-1 rounded-md border border-neutral-200 shadow-[0_2px_10px_rgba(17,18,20,0.06)]">
                  {c.label}
                </span>
                <span className="mx-1.5 sm:mx-2 h-px w-6 sm:w-12 xl:w-20 bg-neutral-400/70 origin-right animate-[line-draw_0.5s_ease-out_forwards]" style={{ animationDelay: `${650 + i * 220}ms` }} />
              </>
            )}
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-30" style={{ backgroundColor: accent }} />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 ring-2 ring-white/80" style={{ backgroundColor: accent }} />
            </span>
            {c.dir === 'right' && (
              <>
                <span className="mx-1.5 sm:mx-2 h-px w-6 sm:w-12 xl:w-20 bg-neutral-400/70 origin-left animate-[line-draw_0.5s_ease-out_forwards]" style={{ animationDelay: `${650 + i * 220}ms` }} />
                <span className="font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.14em] sm:tracking-[0.18em] max-w-[34vw] sm:max-w-none whitespace-normal sm:whitespace-nowrap text-left leading-tight text-neutral-600 bg-white/90 backdrop-blur-sm px-1.5 sm:px-2 py-1 rounded-md border border-neutral-200 shadow-[0_2px_10px_rgba(17,18,20,0.06)]">
                  {c.label}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* inspect hint — fades after first interaction */}
      {showHint && (
        <div
          className="hidden lg:flex absolute top-2 right-2 z-30 items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-neutral-200 pointer-events-none"
          style={{ opacity: hinted ? 0 : 1, transition: 'opacity 0.6s ease' }}
          aria-hidden="true"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-40" style={{ backgroundColor: accent }} />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: accent }} />
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Move cursor to inspect</span>
        </div>
      )}

      {/* floating filmstrip dock */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 flex gap-2 p-2 bg-white/90 backdrop-blur-md border border-neutral-200 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="showcase-thumbs"
      >
        {views.map((v, i) => (
          <button
            key={v.key}
            onClick={() => { setPaused(true); setView(i); }}
            aria-label={`View ${v.label}`}
            title={v.label}
            data-testid={`showcase-thumb-${v.key}`}
            className={`w-12 h-12 md:w-14 md:h-14 rounded-xl p-1.5 border transition-all duration-300 ${
              i === view
                ? 'bg-white scale-105'
                : 'border-transparent bg-neutral-100 hover:bg-white hover:border-neutral-300'
            }`}
            style={i === view ? { borderColor: accent, boxShadow: `0 4px 14px ${accent}2E` } : undefined}
          >
            <img src={v.src} alt={v.label} loading="lazy" className="w-full h-full object-contain" />
          </button>
        ))}
      </div>
    </div>
  );
};
